import { assignInlineVars } from '@vanilla-extract/dynamic';
import { Box, Heading, Inline, Stack, Text } from 'braid-design-system';
import type { ComponentProps, ReactNode } from 'react';

import { ButtonLink, Link } from 'src/components/NavLink/NavLink';
import type { LinkContext } from 'src/modules/seek-jobs-analytics/mappers/link';

import * as styles from './Campaign.css';

export interface MobileCampaignProps {
  type: 'multi-campaign' | 'standalone';
  background: {
    color?: string;
    backgroundImage?: string;
    imageUrl?: string;
    altText?: string;
    centerImage?: boolean;
    imageMinHeightVar?: string;
    backgroundPosition?: string;
    type?: 'full';
  };
  text: {
    title: ReactNode | string;
    subTitle?: {
      label?: ReactNode | string;
      imageUrl?: string;
      color?: string;
      altText?: string;
      backgroundPosition?: string;
    };
    inverted?: boolean;
  };
  reverseLayout?: boolean;
  subTitle?: ReactNode | string;
  alignRight?: boolean;
  cta?: {
    label: string;
    variant?: ComponentProps<typeof ButtonLink>['variant'];
    tone?: ComponentProps<typeof ButtonLink>['tone'];
    inverted?: boolean;
    linkContext?: LinkContext;
  };
  location?: string;
  analytics?: string;
  logoImageUrl?: string;
}

const MobileCampaign = ({
  text: { title, subTitle, inverted },
  background,
  type,
  cta,
  reverseLayout,
  location,
  analytics,
  logoImageUrl,
}: MobileCampaignProps) => {
  const isStandalone = type === 'standalone';

  const content = (
    <Box
      height="full"
      borderRadius="xlarge"
      overflow="hidden"
      background={inverted ? 'customDark' : undefined}
      className={[
        styles.background,
        isStandalone ? styles.standaloneCampaign : styles.multiCampaign,
        reverseLayout ? styles.reverse : undefined,
      ]}
      style={{
        ...assignInlineVars({
          [styles.backgroundColour]: background.color,
          [styles.backgroundImage]: `url('${background.imageUrl}')`,
          [styles.subtitleColour]: subTitle?.color || '',
        }),
        textAlign: 'center',
      }}
    >
      <Box display="flex" height="full" className={styles.containerLayout}>
        <Box flexGrow={1} minWidth={0} width="full">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="spaceBetween"
            alignItems="center"
            height="full"
            className={styles.contentGutter}
          >
            {title || subTitle ? (
              <Stack space="small" align="center">
                {title ? (
                  <Box marginBottom="small">
                    <Text weight="strong">
                      <Box display="block" className={styles.title}>
                        {title}
                      </Box>
                    </Text>
                  </Box>
                ) : null}
                {subTitle && subTitle.imageUrl && isStandalone ? (
                  <Box
                    className={styles.imageMobileSubtitle}
                    style={assignInlineVars({
                      [styles.subtitleBackgroundImage]: `url('${subTitle.imageUrl}')`,
                      [styles.subtitleBackgroundPosition]: `${subTitle.backgroundPosition}`,
                    })}
                    aria-label={`${subTitle.altText}`}
                    role="img"
                  />
                ) : null}
                {subTitle && subTitle.label && isStandalone ? (
                  <Heading level="2">
                    <span className={styles.subtitle}>{subTitle.label}</span>
                  </Heading>
                ) : null}
                {subTitle && !isStandalone ? (
                  <Text size="large">
                    <span className={styles.subtitle}>{subTitle.label}</span>
                  </Text>
                ) : null}
                {logoImageUrl && isStandalone ? (
                  <Box
                    className={styles.imageSubtitle}
                    style={assignInlineVars({
                      [styles.subtitleBackgroundImage]: `url('${logoImageUrl}')`,
                      [styles.subtitleImageHeight]: '50px',
                    })}
                    aria-label="seek and you shall find"
                    role="img"
                  />
                ) : null}
              </Stack>
            ) : null}
            {cta && location ? (
              <Box
                paddingTop={title || subTitle ? 'gutter' : undefined}
                background={cta.inverted ? 'customDark' : undefined}
              >
                <Inline space="none">
                  <ButtonLink
                    variant={cta?.variant}
                    tone={cta?.tone}
                    location={location}
                    analytics={analytics}
                    linkContext={cta.linkContext}
                  >
                    <span className={styles.nowrap}>{cta.label}</span>
                  </ButtonLink>
                </Inline>
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box flexGrow={1} minWidth={0} width="full">
          <Box
            className={styles.image}
            style={assignInlineVars({
              [styles.imageMinHeightVar]: background.imageMinHeightVar,
              [styles.backgroundPosition]: background.backgroundPosition,
            })}
            aria-label={`${background.altText}`}
            role="img"
          />
        </Box>
      </Box>
    </Box>
  );

  return isStandalone || (cta && location) ? (
    content
  ) : (
    <Link location={location} analytics={analytics}>
      {content}
    </Link>
  );
};

export default MobileCampaign;
